import { AxiosError } from 'axios';
import { authenticatedApi } from '../../../shared/api/apiClientUtils';

export type { PlatformOrganisationResponse as PlatformOrganisationDto } from '@billda/api';

export interface PlatformFeatureDto {
  key: string;
  description: string;
  category: string;
  globalOverride: 'enabled' | 'disabled' | null;
}

export interface ListPlatformFeaturesResponse {
  features: PlatformFeatureDto[];
}

export interface CreatePlatformOrganisationRequest {
  organisationName: string;
  ownerEmail: string;
  givenName: string;
  familyName: string;
}

export interface CreatePlatformOrganisationResponse {
  organisationId: string;
  invitationId: string;
}

export interface OrgFeatureSwitchDto {
  key: string;
  description: string;
  category: string;
  effectiveState: 'enabled' | 'disabled';
  source: 'org' | 'global' | 'default';
}

export interface GetPlatformOrganisationFeaturesResponse {
  organisationName: string;
  features: OrgFeatureSwitchDto[];
}

export async function getPlatformOrganisationFeatures(
  orgId: string
): Promise<GetPlatformOrganisationFeaturesResponse | null> {
  const response =
    await authenticatedApi.get<GetPlatformOrganisationFeaturesResponse>(
      `/api/v1/platform/organisations/${orgId}/features`
    );

  return response.data;
}

export async function getPlatformFeatures(
  searchTerm?: string
): Promise<ListPlatformFeaturesResponse | null> {
  const params = new URLSearchParams();
  if (searchTerm) {
    params.set('searchTerm', searchTerm);
  }

  const url = `/api/v1/platform/features${params.toString() ? `?${params}` : ''}`;
  const response =
    await authenticatedApi.get<ListPlatformFeaturesResponse>(url);

  return response.data;
}

export interface SetOverrideRequest {
  isEnabled: boolean;
}

export async function setGlobalOverride(
  key: string,
  request: SetOverrideRequest
): Promise<void> {
  await authenticatedApi.put(
    `/api/v1/features/${encodeURIComponent(key)}/overrides/global`,
    request
  );
}

export async function removeGlobalOverride(key: string): Promise<void> {
  await authenticatedApi.delete(
    `/api/v1/features/${encodeURIComponent(key)}/overrides/global`
  );
}

export async function setOrgOverride(
  key: string,
  orgId: string,
  request: SetOverrideRequest
): Promise<void> {
  await authenticatedApi.put(
    `/api/v1/features/${encodeURIComponent(key)}/overrides/org/${orgId}`,
    request
  );
}

export async function removeOrgOverride(
  key: string,
  orgId: string
): Promise<void> {
  await authenticatedApi.delete(
    `/api/v1/features/${encodeURIComponent(key)}/overrides/org/${orgId}`
  );
}

export async function createPlatformOrganisation(
  request: CreatePlatformOrganisationRequest
): Promise<CreatePlatformOrganisationResponse> {
  try {
    const response =
      await authenticatedApi.post<CreatePlatformOrganisationResponse>(
        '/api/v1/platform/organisations',
        request
      );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 409) {
      throw new Error('A user with this email already exists');
    }
    throw error;
  }
}
